'use client'

import { FieldArray, useField } from 'formik'
import type { ReactNode } from 'react'
import { Info, Plus, X } from 'lucide-react'
import { FormInput } from '@/components/form-inputs/form-input'
import { Button } from '@/components/ui/button'
import { Card as UICard, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { cn } from '@/lib/utils'
import type { NumericField } from './catalog'
import { formatPlanUnit, type PlanPresentation } from './presentation'
import { parseAmount } from './model'
import styles from './plan-editor.module.css'
import { useTranslations } from "next-intl"
import { usePlanPresentation } from "./use-plan-presentation"





export function NumberControl({ field, disabled = false }: { field: NumericField; disabled?: boolean }) {
  const presentation = usePlanPresentation()

  const tUi = useTranslations("Billing.BillingPlanEditorFields")

  const [input, , helpers] = useField<string | null>(field.key)
  const nullable = Boolean(field.empty)
  return (
    <div className="min-w-0 space-y-1.5">
      {nullable ? <>
        <div className="flex items-center justify-between">
          <Label htmlFor={`${field.key}-mode`}>{field.label}</Label>
        </div>
        <Select value={input.value === null ? 'unset' : 'limited'} disabled={disabled} onValueChange={mode => {
          void helpers.setValue(mode === 'unset' ? null : String(field.min ?? 0))
          void helpers.setTouched(true, false)
        }}>
          <SelectTrigger id={`${field.key}-mode`} className="h-auto min-h-10 text-left [&>span]:whitespace-normal"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="unset">{field.empty}</SelectItem>
            <SelectItem value="limited"><span key={field.valueLabel ?? tUi('setALimit')}>{field.valueLabel ?? tUi('setALimit')}</span></SelectItem>
          </SelectContent>
        </Select>
      </> : null}
      {input.value !== null || !nullable ? <FormInput
        name={field.key}
        label={nullable ? tUi("value0Value", { value0: String(field.label) }) : field.label}
        type="text"
        inputMode={field.step && field.step < 1 ? 'decimal' : 'numeric'}
        value={input.value ?? ''}
        disabled={disabled}
        description={[field.hint, unitNote(field, input.value, presentation), input.value === '0' ? field.zero : ''].filter(Boolean).join(' ')}
      /> : <p className="text-xs text-muted-foreground">{field.hint}</p>}
    </div>
  )
}

function unitNote(field: NumericField, value: string | null, presentation: PlanPresentation): string {
  if (!field.unit) return ''
  const hours = field.unit === 'hours' ? parseAmount(value, field) : null
  if (hours !== null && hours >= 24) return presentation.t('hoursUnit', { days: Math.round(hours / 24 * 10) / 10 })
  return presentation.t('unit', { unit: formatPlanUnit(field.unit, 0, presentation).replace(/^0\s?/, '') })
}

export function SectionCard({ title, children, className }: { title: string; children: ReactNode; className?: string }) {
  return <UICard className={cn('min-w-0', className)}><CardHeader className="p-4 pb-3"><CardTitle className="text-base">{title}</CardTitle></CardHeader><CardContent className="space-y-5 p-4 pt-0">{children}</CardContent></UICard>
}

export function FieldGrid({ children }: { children: ReactNode }) {
  return <div className={styles.fields}>{children}</div>
}

/** `onTint`: the card is already violet, so a plain background keeps the notice distinct. */
export function Notice({ children, onTint = false }: { children: ReactNode; onTint?: boolean }) {
  return (
    <div className={cn(
      'flex items-start gap-3 rounded-md border border-violet-200 border-l-4 border-l-violet-600 p-3 text-sm leading-relaxed text-foreground shadow-sm dark:border-violet-800 dark:border-l-violet-400',
      onTint ? 'bg-background' : 'bg-violet-50 dark:bg-violet-950/30',
    )}>
      <Info aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-violet-600 dark:text-violet-400" />
      <p className="min-w-0">{children}</p>
    </div>
  )
}

export function Benefits() {


  const tUi = useTranslations("Billing.BillingPlanEditorFields")

  const [field] = useField<string[]>('benefits')
  return <FieldArray name="benefits">{({ push, remove }) => <div className="space-y-3">
    <p className="text-sm text-muted-foreground"><span key={tUi("textShownOnPricingCardsBenefitsDoNot")}>{tUi("textShownOnPricingCardsBenefitsDoNot")}</span></p>
    {field.value.map((_, index) => <div key={index} className="flex items-start gap-2">
      <FormInput name={`benefits.${index}`} label={tUi("benefitValue0", { value0: String(index + 1) })} containerClassName="min-w-0 flex-1" />
      <Button type="button" variant="ghost" size="icon" className="mt-6 shrink-0" aria-label={tUi("removeBenefitValue0", { value0: String(index + 1) })} onClick={() => remove(index)}><X className="h-4 w-4" /></Button>
    </div>)}
    <Button type="button" variant="outline" onClick={() => push('')}><Plus className="mr-2 h-4 w-4" /><span key={tUi("addBenefit")}>{tUi("addBenefit")}</span></Button>
  </div>}</FieldArray>
}
